import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { ErrorState } from "../../redux/reducers/errors";
import { errorToLoggableObject } from "../../errorHelper";
import { ErrorRenderer } from "../../ModuleProps";
import { ErrorLayout } from "./ErrorLayout";
import { defaultRenderer } from "./defaultRenderer";

window.addEventListener("unhandledrejection", function (event) {
  // the event object has two special properties:
  // alert(event.promise); // [object Promise] - the promise that generated the error
  // alert(event.reason); // Error: Whoops! - the unhandled error object
  console.error("UNHANDLED ERROR", event);
});

type ErrorBoundaryProps = ConnectedProps<typeof connector> & {
  children: any;
  errorRenderers: ErrorRenderer[];
};

type ErrorBoundayState = {
  unhandledError: { [key: string]: any } | null;
};

class _ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundayState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { unhandledError: null };
  }

  static getDerivedStateFromError(error: any) {
    // Update state so the next render will show the fallback UI.
    return { unhandledError: error };
  }

  resolveError() {
    return this.props.error
      ? {
          ...errorToLoggableObject(this.props.error),
          source: "PROPS",
        }
      : this.state.unhandledError
      ? {
          ...errorToLoggableObject(this.state.unhandledError),
          source: "UNHANDLED",
        }
      : null;
  }

  renderError(error: any) {
    const renderers = [...this.props.errorRenderers, defaultRenderer];
    // try any module-specific error renderers first, if nothing handles the error
    //  then we fall back to the default renderer
    for (const renderer of renderers) {
      const handledErrorContent = renderer(error);
      if (handledErrorContent) {
        return handledErrorContent;
      }
    }
  }

  render() {
    const error = this.resolveError();

    if (error) {
      console.log("ERROR:", error);
      return <ErrorLayout>{this.renderError(error)}</ErrorLayout>;
    }

    return this.props.children;
  }
}

const connector = connect((state: { errors: ErrorState }) => ({
  error: state.errors.error,
}));

export const ErrorBoundary = connector(_ErrorBoundary);
